import { useState } from "react";

import { type Compare } from "@features/sales-v3/hooks/use-chart-state";
import { type GetCompareQueryMetadata } from "@features/sales-v3/utils/compare-query";
import {
  safePolygon,
  useDelayGroup,
  useFloating,
  useHover,
  useInteractions,
} from "@floating-ui/react";
import { NBAccountingMode } from "@north-beam/nb-common";

import { MenuItem } from "./menu-item";
import { MenuSubmenuItem } from "./menu-submenu-item";

interface AccountingModeSelectorProps {
  compare: Compare;
  updateCompare: (compare: Compare) => void;
  getCompareQueryMetadata: GetCompareQueryMetadata;
  isDisabled?: boolean;
}

export const AccountingModeSelector = ({
  compare,
  updateCompare,
  getCompareQueryMetadata,
  isDisabled = false,
}: AccountingModeSelectorProps) => {
  const [isHovering, setIsHovering] = useState(false);
  const compareQueryMetadata = getCompareQueryMetadata({
    queryKey: "accountingMode",
  });

  const { refs, floatingStyles, context } = useFloating({
    open: isHovering,
    onOpenChange: setIsHovering,
    transform: false,
    placement: "right-start",
  });

  const { delay } = useDelayGroup(context);

  const hover = useHover(context, {
    delay,
    handleClose: safePolygon({
      requireIntent: false,
      blockPointerEvents: true,
      buffer: 1,
    }),
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  return (
    <div>
      <MenuItem
        isDisabled={isDisabled}
        isHovering={isHovering}
        icon={<span className="mr-2">{compareQueryMetadata.icon}</span>}
        text={compareQueryMetadata.label}
        setReference={refs.setReference}
        {...getReferenceProps()}
      />
      {isHovering && (
        <div
          className="absolute rounded-md bg-white elevation-1 py-1 z-10 text-sm w-56"
          ref={refs.setFloating}
          style={floatingStyles}
          {...getFloatingProps()}
        >
          {NBAccountingMode.reactSelectOptions.map((option) => (
            <MenuSubmenuItem
              key={option.value}
              label={option.label}
              isSelected={
                compare.queryKey === "accountingMode" &&
                compare.queryValue === option.value
              }
              onClick={() =>
                updateCompare({
                  ...compare,
                  queryKey: "accountingMode",
                  queryValue: option.value,
                })
              }
            />
          ))}
        </div>
      )}
    </div>
  );
};
